import React, { useState, useEffect } from 'react';
import { Star, ShoppingCart, Heart, Share2, Minus, Plus, Check, Truck, ShieldCheck, ArrowRight, Loader2, MessageCircle, User, EyeOff, Send, Trash2, Sparkles } from 'lucide-react';
import { useNotification } from './Notification';
import { useNavigate } from 'react-router-dom';
import { Product, Review } from '../types';
import { ProductCard } from './ProductCard';
import { doc, getDoc, collection, query, where, getDocs, db, onSnapshot, addDoc, serverTimestamp, orderBy, updateDoc, increment, deleteDoc } from '../firebase';
import { Footer } from './Footer';
import { useUser } from '../contexts/UserContext';
import { LoginForm } from './LoginForm';
import { OptimizedImage } from './OptimizedImage';

interface ProductDetailsModalProps {
  product: Product;
  isDark: boolean;
  onClose: () => void;
  onAddToCart: (e: React.MouseEvent, product: Product) => void;
  onBuyNow?: (product: Product, quantity: number, color: string, size: string) => void;
  onOpenDetails?: (product: Product) => void;
  addedToCartMap?: { [key: number]: boolean };
  user?: { uid: string; displayName?: string | null; photoURL?: string | null; email?: string | null } | null;
  isAdmin?: boolean;
}

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  product,
  isDark,
  onClose,
  onAddToCart,
  onBuyNow,
  onOpenDetails,
  addedToCartMap = {},
  user,
  isAdmin = false
}) => {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { showNotification } = useNotification();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [activeImage, setActiveImage] = useState(0);
  const [isWishlist, setIsWishlist] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const relatedProductsRef = React.useRef<HTMLDivElement>(null);

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewAnonymous, setReviewAnonymous] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [deletingReviewId, setDeletingReviewId] = useState<string | null>(null);

  // Local Add to Cart Feedback
  const [isAddedLocal, setIsAddedLocal] = useState(false);

  // Swipe logic
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const activeImageRef = React.useRef(0);

  // Initialize selected color from advanced variants
  useEffect(() => {
    if (product.variantColors && product.variantColors.length > 0) {
      setSelectedColor(product.variantColors[0].color);
      if (product.variantColors[0].sizes.length > 0) {
        setSelectedSize(product.variantColors[0].sizes[0].size);
      }
    }
  }, [product.variantColors]);

  // Get available sizes for selected color
  const availableSizes = product.variantColors && product.variantColors.length > 0
    ? product.variantColors.find(vc => vc.color === selectedColor)?.sizes || []
    : [];

  // Get available colors
  const availableColors = product.variantColors && product.variantColors.length > 0
    ? product.variantColors.map(vc => vc.color)
    : ['أزرق', 'كحلي', 'أحمر'];

  // Get images from product.images array or fallback to single image
  const images = product.images && product.images.length > 0
    ? product.images.map(img => img.url)
    : [product.image, product.image, product.image, product.image];

  // Fallback sizes and colors if no advanced variants
  const fallbackSizes = ['S', 'M', 'L', 'XL', 'XXL'];
  const fallbackColors = [
    { name: 'أزرق', hex: '#3b82f6' },
    { name: 'كحلي', hex: '#1e293b' },
    { name: 'أحمر', hex: '#ef4444' }
  ];

  // Update sizes when color changes
  useEffect(() => {
    if (product.variantColors && product.variantColors.length > 0 && selectedColor) {
      const colorVariant = product.variantColors.find(vc => vc.color === selectedColor);
      if (colorVariant && colorVariant.sizes.length > 0) {
        // Reset size if current size not available for new color
        if (!colorVariant.sizes.some(s => s.size === selectedSize)) {
          setSelectedSize(colorVariant.sizes[0].size);
        }
      }
    }
  }, [selectedColor, product.variantColors]);

  // Fetch related products (Deferred for performance)
  const fetchAiRelatedProducts = async () => {
    setLoadingRelated(true);
    try {
      const response = await fetch('/api/ai-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getRelatedProducts',
          payload: {
            productId: String(product.id),
            productName: product.name,
            category: product.category || product.mainCategory
          }
        })
      });

      if (!response.ok) throw new Error('AI Fetch failed');
      const relatedIds = await response.json();

      const relatedPromises = relatedIds.map(async (id: string) => {
        const snap = await getDoc(doc(db, 'products', id));
        return snap.exists() ? { id: snap.id, ...snap.data() } as Product : null;
      });

      const fetched = (await Promise.all(relatedPromises)).filter(p => p !== null) as Product[];
      setRelatedProducts(fetched);
      showNotification('تم تحديث المنتجات بنجاح بالذكاء الاصطناعي', 'success');
    } catch (err) {
      console.error('AI Related error:', err);
      showNotification('فشل استخدام الذكاء الاصطناعي، يرجى المحاولة لاحقاً', 'error');
    } finally {
      setLoadingRelated(false);
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const fetchDefaultRelated = async () => {
      setLoadingRelated(true);
      try {
        if (product.relatedProducts && product.relatedProducts.length > 0) {
          // Fetch specific related products
          const relatedPromises = product.relatedProducts.map(async (id) => {
            try {
              const prodRef = doc(db, 'products', id.toString());
              const snap = await getDoc(prodRef);
              if (snap.exists()) {
                return { id: snap.id, ...snap.data() } as Product;
              }
            } catch (err) {
              console.error(`Error fetching related product ${id}: `, err);
            }
            return null;
          });
          const fetched = (await Promise.all(relatedPromises)).filter(p => p !== null) as Product[];
          setRelatedProducts(fetched);
        } else if (product.mainCategory) {
          // Auto-fetch from same category if no related products
          const q = query(
            collection(db, 'products'),
            where('mainCategory', '==', product.mainCategory),
            where('isActive', '==', true)
          );
          const snapshot = await getDocs(q);
          const fetched = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as Product))
            .filter(p => p.id !== product.id)
            .slice(0, 4);
          setRelatedProducts(fetched);
        }
      } catch (err) {
        console.error("Error fetching related products:", err);
      } finally {
        setLoadingRelated(false);
      }
    };

    timeout = setTimeout(() => {
      fetchDefaultRelated();
    }, 100);

    return () => clearTimeout(timeout);
  }, [product.id, product.relatedProducts, product.mainCategory]);

  // Fetch reviews for this product (Deferred for performance)
  useEffect(() => {
    let unsub: any;
    let timeout: NodeJS.Timeout;

    setLoadingReviews(true);

    timeout = setTimeout(() => {
      try {
        const reviewsRef = collection(db, 'reviews');
        const q = query(reviewsRef, where('productId', '==', String(product.id)));
        unsub = onSnapshot(q, (snap) => {
          const list: Review[] = [];
          snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as Review));
          list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setReviews(list);
          setLoadingReviews(false);
        }, (err) => {
          console.error('Error loading reviews:', err);
          setLoadingReviews(false);
        });
      } catch (err) {
        console.error('Error loading reviews:', err);
        setLoadingReviews(false);
      }
    }, 200);

    // Safety timeout: stop loading after 5s even if query hangs
    const safetyTimeout = setTimeout(() => {
      setLoadingReviews(false);
    }, 5000);

    return () => {
      if (timeout) clearTimeout(timeout);
      if (unsub) unsub();
      clearTimeout(safetyTimeout);
    };
  }, [product.id]);

  const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : product.rating;

  const submitReview = async () => {
    if (!user) { showNotification('يجب تسجيل الدخول لإضافة تقييم', 'warning'); return; }
    if (profile?.isGuest) { setShowLoginModal(true); return; }
    if (!reviewComment.trim()) { showNotification('يرجى كتابة تعليق', 'warning'); return; }
    setSubmittingReview(true);
    try {
      await addDoc(collection(db, 'reviews'), {
        productId: String(product.id),
        orderId: '',
        userId: user.uid,
        userName: reviewAnonymous ? 'مجهول' : (user.displayName || 'مستخدم'),
        userPhoto: reviewAnonymous ? '' : (user.photoURL || ''),
        isAnonymous: reviewAnonymous,
        rating: reviewRating,
        comment: reviewComment.trim(),
        createdAt: serverTimestamp()
      });

      // Update Product Avg Rating & Review Count
      const productRef = doc(db, 'products', String(product.id));
      const currentReviewCount = reviews.length; // Before this new one is processed by snapshot
      const currentRating = reviews.length > 0
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : (product.adminRating || product.rating || 5);

      const newReviewCount = currentReviewCount + 1;
      const newRating = ((currentRating * currentReviewCount) + reviewRating) / newReviewCount;

      await updateDoc(productRef, {
        rating: Number(newRating.toFixed(1)),
        reviewCount: increment(1)
      });

      setReviewComment('');
      setReviewRating(5);
      setReviewAnonymous(false);
    } catch (err: any) {
      showNotification(err.message || 'حدث خطأ', 'error');
    } finally {
      setSubmittingReview(false);
    }
  };

  const deleteReview = async (reviewId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا التقييم؟')) return;
    setDeletingReviewId(reviewId);
    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
    } catch (err) {
      console.error('Error deleting review:', err);
      showNotification('حدث خطأ أثناء حذف التقييم', 'error');
    } finally {
      setDeletingReviewId(null);
    }
  };

  const handleQuantity = (delta: number) => {
    setQuantity(prev => Math.max(1, prev + delta));
  };

  const totalPrice = product.price * quantity;

  // Swiping handlers
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchStart(clientX);
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (touchStart === null) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchEnd(clientX);
    if (Math.abs(clientX - touchStart) > 10) {
      setIsDragging(true);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isRightSwipe && activeImage < images.length - 1) { // Swipe right to reveal image on the left (RTL)
      setActiveImage(prev => prev + 1);
    } else if (isLeftSwipe && activeImage > 0) { // Swipe left to reveal image on the right (RTL)
      setActiveImage(prev => prev - 1);
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/?product=${product.id}`;
      await navigator.clipboard.writeText(shareUrl);
      showNotification('تم نسخ الرابط بنجاح!', 'success');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <div className={`w-full max-w-[1400px] mx-auto animate-fade-in-up px-2 md:px-6 pb-[72px] page-transition ${isDark ? 'text-white' : 'text-slate-900'}`}>

      {/* Back Button Header - Relative (not sticky) to avoid conflict with main sticky header */}
      <div className="w-full flex justify-end py-4 mb-2 relative z-40" style={{ direction: 'ltr' }}>
        <button
          onClick={onClose}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-full font-black text-sm md:text-lg transition-all active:scale-95 border hover:scale-105 shadow-lg backdrop-blur-md ${isDark ? 'bg-black/40 text-white border-white/20 hover:bg-white hover:text-black' : 'bg-white/80 text-slate-800 border-slate-200 hover:bg-slate-800 hover:text-white'}`}
        >
          <span>عودة</span>
          <ArrowRight size={20} className="transform rotate-0" />
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-12 mb-12" style={{ direction: 'rtl' }}>

        {/* --- LEFT COLUMN: IMAGE GALLERY --- */}
        <div className="w-full lg:w-[48%] flex flex-col gap-4">
          <div className={`relative w-full aspect-[4/5] sm:aspect-square rounded-[2rem] md:rounded-[2.5rem] overflow-hidden group border ${isDark ? 'bg-[#13151a] border-white/5' : 'bg-white border-slate-100 shadow-sm'} p-1`}>
            {/* Discount Badge */}
            {product.discount && (
              <div className="absolute top-4 right-4 z-10 bg-red-500 text-white text-[10px] md:text-sm font-black px-3 py-1 md:px-4 md:py-1.5 rounded-full shadow-lg">
                خصم {product.discount}%
              </div>
            )}

            {/* Gift Points Badge */}
            {product.giftPoints && product.giftPoints > 0 ? (
              <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[10px] md:text-sm font-black px-3 py-1 md:px-4 md:py-1.5 rounded-full shadow-lg flex items-center gap-1.5 border border-amber-300/50">
                <Sparkles size={14} className="text-amber-100 animate-pulse" />
                <span>تكسب {product.giftPoints} نقطة</span>
              </div>
            ) : null}

            <div className={`absolute inset-0 transition-all duration-500 ${isDark ? 'bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.1)_0%,transparent_70%)]' : 'bg-gradient-to-tr from-blue-100/50 to-transparent'}`}></div>

            <div
              className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing relative z-10"
              onMouseDown={handleTouchStart}
              onMouseMove={handleTouchMove}
              onMouseUp={handleTouchEnd}
              onMouseLeave={handleTouchEnd}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <OptimizedImage
                src={images[activeImage]}
                alt={product.name}
                priority={true}
                aspectRatio="1/1"
                className={`w-full h-full p-8 md:p-12 transition-transform duration-300 ease-out group-hover:scale-110 select-none`}
                containerBackground="transparent"
                objectFit="contain"
              />
            </div>

            {/* Action Buttons */}
            <div className="absolute bottom-6 left-6 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-4 group-hover:translate-y-0">
              <button
                onClick={() => setIsWishlist(!isWishlist)}
                className={`p-3.5 rounded-full shadow-lg backdrop-blur-md transition-all hover:scale-110 border ${isDark ? 'bg-black/40 border-white/10 text-white' : 'bg-white/90 border-slate-200 text-slate-700'} ${isWishlist ? 'text-red-500 fill-red-500' : ''}`}
              >
                <Heart size={22} className={isWishlist ? 'fill-red-500 text-red-500' : ''} />
              </button>
              <button
                onClick={handleShare}
                className={`p-3.5 rounded-full shadow-lg backdrop-blur-md transition-all hover:scale-110 border ${isDark ? 'bg-black/40 border-white/10 text-white' : 'bg-white/90 border-slate-200 text-slate-700'}`}
              >
                <Share2 size={22} />
              </button>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="flex gap-4 overflow-x-auto pb-4 pt-2 no-scrollbar justify-center lg:justify-start px-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImage(idx)}
                className={`relative w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden border-2 transition-all duration-300 shrink-0 ${activeImage === idx ? 'border-blue-500 scale-110 shadow-blue-500/40 shadow-xl z-10' : (isDark ? 'border-white/10 opacity-60 hover:opacity-100 bg-white/5' : 'border-slate-200 opacity-60 hover:opacity-100 bg-white')}`}
              >
                <OptimizedImage
                  src={img}
                  alt="thumb"
                  aspectRatio="1/1"
                  objectFit="contain"
                  className={`w-full h-full p-2 ${isDark ? 'drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]' : ''}`}
                  containerBackground={isDark ? 'rgba(255,255,255,0.05)' : '#ffffff'}
                />
              </button>
            ))}
          </div>
        </div>

        {/* --- RIGHT COLUMN: PRODUCT DETAILS --- */}
        <div className="flex-1 flex flex-col pt-2">

          {/* Header */}
          <div className="mb-4 md:mb-8 text-center md:text-right">
            <div className="flex items-center justify-center md:justify-start gap-2 md:gap-3 mb-3">
              <span className={`text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-lg border ${isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                الأكثر مبيعاً
              </span>
              <span className={`text-[10px] md:text-xs font-bold ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                المجموعة الرسمية
              </span>
            </div>

            <h1 className="text-2xl md:text-5xl font-black leading-tight mb-3">
              {product.name}
            </h1>

            <div className="flex flex-col items-center md:items-start gap-1">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="flex items-center text-yellow-500 gap-1 bg-yellow-500/10 px-2 py-0.5 rounded-lg border border-yellow-500/20">
                  <span className="font-black text-lg">{avgRating.toFixed(1)}</span>
                  <Star size={14} fill="currentColor" className="text-yellow-500" />
                </div>
                <span className={`text-xs md:text-sm font-black transition-colors ${reviews.length > 0 ? 'text-blue-500 underline decoration-dotted' : (isDark ? 'text-slate-400' : 'text-slate-500')}`}>
                  {reviews.length > 0 ? `${reviews.length} تقييم حقيقي` : 'بناءً على توصيات رشيد أونلاين'}
                </span>
              </div>
            </div>
          </div>

          {/* Price Card */}
          <div className={`p-6 md:p-8 rounded-[2rem] mb-8 flex flex-wrap items-end gap-4 border shadow-sm transition-all hover:shadow-md ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-100'}`}>
            <div className="flex flex-col">
              <span className={`text-base font-bold line-through decoration-red-500/60 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {product.oldPrice ? product.oldPrice.toLocaleString() : (product.price * 1.3).toLocaleString()} ج.م
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-5xl md:text-6xl font-black text-blue-500 tracking-tight">
                  {product.price.toLocaleString()}
                </span>
                <span className={`text-lg font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>جنية مصري</span>
              </div>
            </div>
            <div className="mr-auto self-center">
              <span className="text-sm font-bold text-green-500 flex items-center gap-1.5 bg-green-500/10 px-4 py-2 rounded-full border border-green-500/20">
                <ShieldCheck size={18} />
                متوفر في المخزون
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="mb-10 text-center md:text-right">
            <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>الوصف</h3>
            <p className={`text-base md:text-lg leading-loose font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {product.description}
            </p>
          </div>

          {/* Selectors - CENTERED */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            {/* Colors */}
            {product.showColorSelector !== false && (
              <div className="flex flex-col items-center">
                <span className={`block text-base font-bold mb-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>اللون: <span className="text-blue-500">{selectedColor || 'اختر لون'}</span></span>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  {product.variantColors && product.variantColors.length > 0 ? (
                    availableColors.map((colorName) => (
                      <button
                        key={colorName}
                        onClick={() => setSelectedColor(colorName)}
                        className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${selectedColor === colorName
                          ? 'bg-blue-500 text-white border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)] scale-105'
                          : (isDark ? 'bg-white/5 border-white/10 text-white hover:border-white/30' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400')
                          }`}
                      >
                        {selectedColor === colorName && <Check size={14} className="inline mr-1" />}
                        {colorName}
                      </button>
                    ))
                  ) : (
                    fallbackColors.map((color) => (
                      <button
                        key={color.name}
                        onClick={() => setSelectedColor(color.name)}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${selectedColor === color.name
                          ? 'shadow-[0_0_15px_rgba(59,130,246,0.5)] scale-110 ring-2 ring-blue-400 ring-offset-2 ring-offset-transparent'
                          : 'shadow-sm hover:scale-110'
                          }`}
                        style={{ backgroundColor: color.hex }}
                      >
                        {selectedColor === color.name && <Check size={20} className="text-white drop-shadow-md" strokeWidth={3} />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Sizes */}
            {product.showSizeSelector !== false && (
              <div className="flex flex-col items-center">
                <span className={`block text-base font-bold mb-4 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>المقاس: <span className="text-blue-500">{selectedSize || 'اختر مقاس'}</span></span>
                <div className="flex flex-wrap justify-center gap-3">
                  {(product.variantColors && product.variantColors.length > 0 && availableSizes.length > 0 ? availableSizes : fallbackSizes.map(s => ({ size: s, stock: 999 }))).map((sizeObj) => {
                    const size = typeof sizeObj === 'string' ? sizeObj : sizeObj.size;
                    const stock = typeof sizeObj === 'object' ? sizeObj.stock : 999;
                    const isOutOfStock = stock <= 0 && !product.ignoreStock;
                    return (
                      <button
                        key={size}
                        onClick={() => !isOutOfStock && setSelectedSize(size)}
                        disabled={isOutOfStock}
                        className={`h-12 min-w-[50px] px-4 rounded-2xl text-base font-bold border transition-all ${selectedSize === size
                          ? 'bg-blue-500/5 text-blue-500 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.5)] transform scale-105'
                          : isOutOfStock
                            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                            : (isDark ? 'bg-white/5 border-white/10 text-slate-400 hover:border-white/30 hover:text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600')
                          }`}
                      >
                        {size} {isOutOfStock && '(نفذ)'}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto"></div>
          <div className={`w-full h-px mb-8 ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}></div>

          {/* Footer Actions */}
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className={`flex items-center gap-4 p-2 rounded-full border ${isDark ? 'bg-black/30 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                <button onClick={() => handleQuantity(-1)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-white hover:bg-slate-200 shadow-sm'}`}>
                  <Minus size={18} />
                </button>
                <span className="w-10 text-center font-black text-xl">{quantity}</span>
                <button onClick={() => handleQuantity(1)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-white hover:bg-slate-200 shadow-sm'}`}>
                  <Plus size={18} />
                </button>
              </div>
              <div className="text-right">
                <p className={`text-sm font-bold mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>الإجمالي الكلي</p>
                <p className="text-2xl font-black text-blue-500">{totalPrice.toLocaleString()} ج.م</p>
              </div>
            </div>

            <div className="flex gap-3 h-14 md:h-16">
              <button
                onClick={(e) => {
                  const enhancedProduct = {
                    ...product,
                    selectedColor: selectedColor,
                    selectedSize: selectedSize
                  };
                  onAddToCart(e, enhancedProduct);

                  setIsAddedLocal(true);
                  setTimeout(() => setIsAddedLocal(false), 2000);

                  // Show toast and scroll to related products
                  setTimeout(() => {
                    if (relatedProductsRef.current) {
                      relatedProductsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className={`flex-[2] transition-all duration-500 rounded-2xl flex items-center justify-center gap-2 md:gap-3 text-white font-black text-sm md:text-xl shadow-xl group active:scale-[0.98] ${isAddedLocal ? 'bg-emerald-500 shadow-emerald-500/25' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 bg-[length:200%_auto] hover:bg-right shadow-blue-500/25'}`}
              >
                {isAddedLocal ? (
                  <>
                    <Check size={20} strokeWidth={3} className="md:w-6 md:h-6" />
                    <span>تمت الإضافة</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart size={20} className="md:w-6 md:h-6 group-hover:animate-bounce" />
                    <span>إضافة للسلة</span>
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  const buyNowItem = {
                    ...product,
                    quantity: quantity,
                    selectedColor: selectedColor,
                    selectedSize: selectedSize,
                    selected: true
                  };

                  if (onBuyNow) {
                    onBuyNow(product, quantity, selectedColor, selectedSize);
                  } else {
                    onClose();
                    navigate('/checkout', { state: { buyNowItem } });
                  }
                }}
                className={`flex-1 px-2 md:px-4 rounded-2xl font-black text-sm md:text-lg border-2 transition-all active:scale-[0.98] ${isDark ? 'border-white/20 text-white hover:bg-white hover:text-black' : 'border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'}`}>
                شراء الآن
              </button>
            </div>

            {/* Dynamic Features or Fallback */}
            <div className="flex items-center justify-center gap-4 md:gap-8 mt-4 pt-6 border-t border-dashed border-gray-700/30 flex-wrap">
              {product.features && product.features.length > 0 ? (
                product.features.map(feature => (
                  <div key={feature.id} className={`flex items-center gap-2 text-xs font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <span className="text-xl md:text-2xl">{feature.icon}</span>
                    <span>{feature.text}</span>
                  </div>
                ))
              ) : (
                <>
                  <div className={`flex items-center gap-2 text-xs font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Truck size={18} className="text-blue-500" />
                    <span>شحن سريع</span>
                  </div>
                  <div className={`flex items-center gap-2 text-xs font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <ShieldCheck size={18} className="text-green-500" />
                    <span>دفع آمن 100%</span>
                  </div>
                  <div className={`flex items-center gap-2 text-xs font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    <Check size={18} className="text-blue-500" />
                    <span>ضمان استرجاع</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <div ref={relatedProductsRef} data-related-products className="mt-12 mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={fetchAiRelatedProducts}
              disabled={loadingRelated}
              className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-black text-sm transition-all active:scale-95 shadow-lg ${isDark ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-600/30' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-600/20'}`}
            >
              <Sparkles size={16} className={loadingRelated ? 'animate-spin' : ''} />
              <span>AI تلقائي بالـ</span>
            </button>
            <h3 className={`text-2xl md:text-3xl font-black text-center md:text-right ${isDark ? 'text-white' : 'text-slate-900'}`}>
              منتجات ذات صلة
            </h3>
          </div>
          {loadingRelated ? (
            <div className="flex justify-center py-10">
              <Loader2 className="animate-spin text-blue-500 w-10 h-10" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ direction: 'rtl' }}>
              {relatedProducts.map(relatedProduct => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  isDark={isDark}
                  onOpenDetails={onOpenDetails || (() => { })}
                  onAddToCart={onAddToCart}
                  isAdded={!!addedToCartMap[relatedProduct.id as number]}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── REVIEWS SECTION ─── */}
      <div className="mt-12 mb-8" style={{ direction: 'rtl' }}>
        <div className="flex items-center gap-3 mb-6">
          <MessageCircle size={24} className="text-blue-500" />
          <h3 className={`text-2xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-slate-900'}`}>
            التقييمات والمراجعات
          </h3>
          <span className={`px-3 py-1 rounded-full text-sm font-bold ${isDark ? 'bg-white/5 text-white/50' : 'bg-slate-100 text-slate-500'}`}>
            {reviews.length}
          </span>
        </div>

        {/* Average Rating Card */}
        <div className={`p-6 rounded-2xl border mb-6 flex flex-col md:flex-row items-center gap-6 ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="text-center">
            <div className="text-5xl font-black text-blue-500">{avgRating.toFixed(1)}</div>
            <div className="flex items-center gap-0.5 mt-2 justify-center">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={18} fill={i <= Math.round(avgRating) ? '#f59e0b' : 'none'} className={i <= Math.round(avgRating) ? 'text-yellow-400' : isDark ? 'text-slate-600' : 'text-slate-300'} />
              ))}
            </div>
            <p className={`text-xs font-bold mt-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{reviews.length} تقييم</p>
          </div>
          <div className="flex-1 w-full space-y-1.5">
            {[5, 4, 3, 2, 1].map(star => {
              const count = reviews.filter(r => Math.round(r.rating) === star).length;
              const pct = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2">
                  <span className={`text-xs font-bold w-6 text-left ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{star}</span>
                  <Star size={12} className="text-yellow-400" fill="#f59e0b" />
                  <div className={`flex-1 h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                    <div className="h-full bg-yellow-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-xs font-bold w-8 text-left ${isDark ? 'text-white/30' : 'text-slate-400'}`}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Review Form */}
        <div className={`p-5 rounded-2xl border mb-6 ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
          <h4 className={`font-black mb-4 ${isDark ? 'text-white' : 'text-slate-800'}`}>أضف تقييمك</h4>

          {/* Star Picker */}
          <div className="flex items-center gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(i => (
              <button
                key={i}
                type="button"
                onMouseEnter={() => setHoverRating(i)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setReviewRating(i)}
                className="transition-transform hover:scale-125"
              >
                <Star
                  size={28}
                  fill={i <= (hoverRating || reviewRating) ? '#f59e0b' : 'none'}
                  className={`transition-colors ${i <= (hoverRating || reviewRating) ? 'text-yellow-400' : isDark ? 'text-slate-600' : 'text-slate-300'}`}
                />
              </button>
            ))}
            <span className={`text-sm font-bold mr-2 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{reviewRating}/5</span>
          </div>

          {/* Comment */}
          <textarea
            value={reviewComment}
            onChange={e => setReviewComment(e.target.value)}
            placeholder="اكتب تعليقك هنا..."
            rows={3}
            className={`w-full px-4 py-3 rounded-xl border text-sm font-bold resize-none outline-none focus:border-blue-500 transition-all ${isDark ? 'bg-black/30 border-white/10 text-white placeholder:text-white/20' : 'bg-slate-50 border-slate-200 placeholder:text-slate-400'}`}
          />

          {/* Options row */}
          <div className="flex items-center justify-between mt-3">
            <label className={`flex items-center gap-2 cursor-pointer text-sm font-bold ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
              <input type="checkbox" checked={reviewAnonymous} onChange={e => setReviewAnonymous(e.target.checked)} className="w-4 h-4 accent-blue-500 rounded" />
              <EyeOff size={14} />
              تعليق كمجهول
            </label>

            <button
              onClick={submitReview}
              disabled={submittingReview || !reviewComment.trim()}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95 ${submittingReview || !reviewComment.trim()
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20'
                }`}
            >
              {submittingReview ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {submittingReview ? 'جاري الإرسال...' : 'إرسال التقييم'}
            </button>
          </div>
        </div>

        {/* Reviews List */}
        {loadingReviews ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-blue-500" size={30} />
          </div>
        ) : reviews.length > 0 ? (
          <div className="space-y-4">
            {reviews.map(review => (
              <div key={review.id} className={`p-5 rounded-2xl border transition-all hover:shadow-md ${isDark ? 'bg-[#1a1c1e] border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 flex-shrink-0 flex items-center justify-center ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-100'}`}>
                    {review.userPhoto && !review.isAnonymous ? (
                      <img src={review.userPhoto} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={20} className={isDark ? 'text-white/30' : 'text-slate-400'} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-black text-sm ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {review.isAnonymous ? 'مجهول' : review.userName}
                      </span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} size={12} fill={i <= review.rating ? '#f59e0b' : 'none'} className={i <= review.rating ? 'text-yellow-400' : 'text-gray-300'} />
                        ))}
                      </div>
                      {review.createdAt && (
                        <span className={`text-[10px] font-bold ${isDark ? 'text-white/20' : 'text-slate-400'}`}>
                          {new Date(review.createdAt.seconds * 1000).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-medium mt-2 leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                      {review.comment}
                    </p>
                  </div>

                  {/* Admin Delete Button */}
                  {isAdmin && (
                    <button
                      onClick={() => deleteReview(review.id)}
                      disabled={deletingReviewId === review.id}
                      className="shrink-0 p-2 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-500 transition-all"
                      title="حذف التقييم"
                    >
                      {deletingReviewId === review.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`text-center py-10 rounded-2xl border ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-100 bg-slate-50'}`}>
            <MessageCircle size={36} className={isDark ? 'text-white/10 mx-auto mb-3' : 'text-slate-300 mx-auto mb-3'} />
            <p className={`font-bold text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>لا توجد تقييمات حتى الآن. كن أول من يقيّم!</p>
          </div>
        )}
      </div>

      {/* Footer in Product Page */}
      <div className="w-full mt-8 relative z-20">
        <Footer isDark={isDark} />
      </div>

      {/* Guest Login/Upgrade Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowLoginModal(false)}></div>
          <div className="relative w-full max-w-md animate-in fade-in zoom-in duration-300">
            <div className={`p-4 rounded-t-[2rem] text-center font-black ${isDark ? 'bg-blue-900 text-white' : 'bg-blue-600 text-white'}`}>
              قم بإنشاء حساب لإكمال العملية
            </div>
            <div className={`p-2 rounded-b-[2rem] shadow-xl ${isDark ? 'bg-black/90' : 'bg-white/90'}`}>
              <LoginForm isDark={isDark} onLoginSuccess={() => setShowLoginModal(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
